import type { CurrencyIdentifier } from "@opensea/app-kit"
import { ChainBadge, NATIVE_CURRENCY_ADDRESS } from "@opensea/app-kit"
import {
  Badge,
  classNames,
  FlexCenter,
  FlexColumn,
  FlexEnd,
  ItemAvatar,
  ItemContent,
  ItemDescription,
  ItemSide,
  ItemTitle,
  NumberDisplay,
  RadioGroup,
  RadioGroupItem,
  Separator,
  Skeleton,
  SkeletonCircle,
  SpaceBetween,
  TextBody,
  TextLabel,
  Tooltip,
} from "@opensea/ui-kit"
import { ArrowRightAlt, ErrorFilled, Info } from "@opensea/ui-kit/icons"
import { range } from "es-toolkit/math"
import type { FragmentOf } from "gql.tada"
import { readFragment } from "gql.tada"
import { useEffect, useState } from "react"
import { useTranslations } from "use-intl"
import { useSnapshot } from "valtio"
import { Button } from "../../../../../components/Button"
import { DestinationWalletSelect } from "../../../../../components/DestinationWalletSelect/DestinationWalletSelect"
import type { WalletDestination } from "../../../../../components/DestinationWalletSelect/types"
import { Item } from "../../../../../components/Item"
import { SmartContractWalletWarningModal } from "../../../../../components/SmartContractWalletWarningModal"
import { useAddressCompatibility } from "../../../../../hooks/useAddressCompatibility"
import { useAddress } from "../../../../../polychain/hooks"
import { useAccount } from "../../../../../polychain/hooks/useAccount"
import type { Balance } from "../../../../../providers/BalancesProvider"
import { FlowState } from "../../../../../stores/flowStore"
import { useSufficientBalances } from "../../../../../utils/balanceUtils"
import type { CheckoutFlowState } from "../../../../flow-state"
import { useIsSmartContractWallet } from "../../../../hooks/useIsSmartContractWallet"
import { itemVariants } from "../../../../PaymentSection/constants"
import { CurrencySelector_relayerFulfillmentFragment } from "./CurrencySelector.fragment"

type CurrencySelectorProps = {
  className?: string
  onSelect: (currencyIdentifier: CurrencyIdentifier) => void
  totalUsdPrice: number
  nftChain: string
  relayerFulfillment?: FragmentOf<
    typeof CurrencySelector_relayerFulfillmentFragment
  > | null
  defaultCurrency: CurrencyIdentifier
  onWalletSelect?: (walletDestination: WalletDestination) => void
  walletDestination?: WalletDestination
  onBalancesReady: () => void
}

const NON_EXPANDED_BALANCES_LIMIT = 1

export function CurrencySelector({
  className,
  onSelect,
  totalUsdPrice,
  nftChain,
  relayerFulfillment: relayerFulfillmentKey,
  defaultCurrency,
  onWalletSelect,
  walletDestination,
  onBalancesReady,
}: CurrencySelectorProps) {
  const t = useTranslations("wallet.CurrencySelector")
  const { connectorId } = useAccount()
  const currentAddress = useAddress()
  const { flow } = useSnapshot(FlowState)
  const isClaim = (flow as CheckoutFlowState)?.isClaim ?? false
  const relayerFulfillment = readFragment(
    CurrencySelector_relayerFulfillmentFragment,
    relayerFulfillmentKey,
  )

  const onlyCurrency = relayerFulfillment ? undefined : defaultCurrency

  const { balances: allValidBalances, loading } = useSufficientBalances({
    totalUsdPrice,
    onlyCurrency,
    preferredCurrency: defaultCurrency,
    targetChain: nftChain,
  })

  // Filter to only native tokens when in claim mode
  const validBalances = isClaim
    ? allValidBalances.filter(
        balance => balance.currency.contractAddress === NATIVE_CURRENCY_ADDRESS,
      )
    : allValidBalances

  const [selectedBalance, setSelectedBalance] = useState<Balance | undefined>(
    undefined,
  )

  // Auto-select first valid balance from the ordered list when balances become available
  useEffect(() => {
    if (validBalances.length > 0 && !selectedBalance) {
      const balanceToSelect = validBalances[0]
      setSelectedBalance(balanceToSelect)
      onSelect({
        contractAddress: balanceToSelect.currency.contractAddress,
        chainIdentifier: balanceToSelect.currency.chainIdentifier,
      })
    }
  }, [validBalances, selectedBalance, onSelect])

  const [expanded, setExpanded] = useState(false)

  const isPhantomAbstractWarning =
    connectorId === "app.phantom" && nftChain === "abstract"

  const handleSelect = (balance?: Balance) => {
    if (isSmartContractWallet) {
      setSmartContractWalletWarningOpen(true)
      return
    }

    if (balance) {
      onSelect({
        contractAddress: balance.currency.contractAddress,
        chainIdentifier: balance.currency.chainIdentifier,
      })
    }
  }

  const onValueChange = (balanceId: string) => {
    const balance = validBalances.find(b => b.id === balanceId)
    setSelectedBalance(balance)

    // Only call handleSelect immediately if not in expanded mode
    if (!expanded) {
      handleSelect(balance)
    }
  }

  const isSmartContractWallet = useIsSmartContractWallet()
  const [smartContractWalletWarningOpen, setSmartContractWalletWarningOpen] =
    useState(false)
  // Show the "Change" button if there are more currencies available
  const hasMoreOptions = validBalances.length > NON_EXPANDED_BALANCES_LIMIT

  // Check if selected currency chain is incompatible with NFT chain
  const isAddressCompatible = useAddressCompatibility(currentAddress, nftChain)

  useEffect(() => {
    if (!loading) {
      onBalancesReady()
    }
  }, [loading])

  if (loading) {
    return <CurrencySelectorSkeleton className={className} />
  }

  if (validBalances.length === 0) {
    return null
  }

  const title = expanded ? (
    <Tooltip content={<div className="max-w-[400px]">{t("tooltip")}</div>}>
      <FlexCenter className="gap-2 text-text-secondary">
        <TextLabel size="xs">{t("paymentMethod")}</TextLabel>
        <ArrowRightAlt size={16} />
        <TextLabel color="text-secondary" size="xs">
          {t("selectToken")}
        </TextLabel>
        <Info size={14} />
      </FlexCenter>
    </Tooltip>
  ) : (
    <SpaceBetween className="w-full items-center">
      <Tooltip content={<div className="max-w-[400px]">{t("tooltip")}</div>}>
        <TextLabel
          className="flex items-center gap-1"
          color="text-secondary"
          size="xs"
          weight="semibold"
        >
          {t("token")}
          <Info size={14} />
        </TextLabel>
      </Tooltip>

      {!expanded && hasMoreOptions && (
        <Button onClick={() => setExpanded(true)} size="sm" variant="secondary">
          {t("change")}
        </Button>
      )}
    </SpaceBetween>
  )

  const renderItems = (count?: number) => {
    // Get the balances to show, respecting the count limit but ensuring selected balance logic
    const getBalancesToShow = () => {
      if (!count) {
        return validBalances
      }

      // If no balance is selected, show top balances as normal
      if (!selectedBalance) {
        return validBalances.slice(0, count)
      }

      // Always put selected balance first, then fill remaining slots with other balances
      return [
        selectedBalance,
        ...validBalances
          .filter(b => b.id !== selectedBalance.id)
          .slice(0, count - 1),
      ]
    }

    const balancesToShow = getBalancesToShow()

    return (
      <RadioGroup
        className="overflow-y-auto"
        onValueChange={onValueChange}
        value={selectedBalance?.id}
      >
        {balancesToShow.map(balance => (
          <Item
            className={itemVariants()}
            key={balance.id}
            onClick={() => onValueChange(balance.id)}
          >
            <ItemAvatar
              badge={
                <ChainBadge
                  identifier={balance.currency.chainIdentifier}
                  size="xs"
                />
              }
              src={balance.currency.imageUrl}
            />
            <ItemContent>
              <ItemTitle size="sm">{balance.currency.name}</ItemTitle>
              <ItemDescription className="font-mono" size="xs">
                {t("balance")}&nbsp;
                <NumberDisplay
                  suffix={balance.currency.symbol}
                  value={balance.quantity ?? 0}
                />
              </ItemDescription>
            </ItemContent>
            <ItemSide>
              <RadioGroupItem value={balance.id} />
            </ItemSide>
          </Item>
        ))}
      </RadioGroup>
    )
  }

  return (
    <>
      {/* Expanded mode should take up the whole container */}
      <FlexColumn
        className={classNames(
          "absolute inset-0 z-1 gap-4 bg-bg-app",
          !expanded && "hidden",
        )}
      >
        {title}
        {renderItems()}

        <Separator className="my-2" />
        <FlexEnd className="w-full">
          <Button
            onClick={() => {
              setExpanded(false)
              handleSelect(selectedBalance)
            }}
            size="sm"
          >
            {t("save")}
          </Button>
        </FlexEnd>
      </FlexColumn>
      <FlexColumn
        className={classNames(
          "gap-4",
          expanded && "invisible min-h-[392px]",
          className,
        )}
      >
        {title}
        {renderItems(NON_EXPANDED_BALANCES_LIMIT)}

        {onWalletSelect && !isAddressCompatible && (
          <DestinationWalletSelect
            className="mt-4"
            destinationChain={nftChain}
            label={
              <div className="mb-2">
                <TextLabel color="text-secondary" weight="semibold">
                  {t("destinationWallet")}
                </TextLabel>
              </div>
            }
            onWalletSelect={onWalletSelect}
            walletDestination={walletDestination}
          />
        )}

        {isPhantomAbstractWarning && (
          <FlexColumn className="rounded-xl border border-border-2 p-3">
            <Badge
              className="mb-2 self-start"
              icon={ErrorFilled}
              variant="warning"
            >
              {t("warning")}
            </Badge>
            <TextBody color="text-secondary" size="sm">
              {t("phantomAbstractWarning")}
            </TextBody>
          </FlexColumn>
        )}
      </FlexColumn>
      <SmartContractWalletWarningModal
        onCancel={() => setSmartContractWalletWarningOpen(false)}
        onContinue={() => {
          if (!selectedBalance) {
            return
          }

          onSelect({
            contractAddress: selectedBalance.currency.contractAddress,
            chainIdentifier: selectedBalance.currency.chainIdentifier,
          })
        }}
        open={smartContractWalletWarningOpen}
      />
    </>
  )
}

function CurrencySelectorSkeleton({ className }: { className?: string }) {
  const t = useTranslations("wallet.CurrencySelector")

  return (
    <FlexColumn className={classNames("mt-4 gap-4", className)}>
      <SpaceBetween className="w-full items-center">
        <TextLabel
          className="flex items-center gap-1"
          color="text-secondary"
          size="xs"
          weight="semibold"
        >
          {t("token")}
          <Info size={14} />
        </TextLabel>
      </SpaceBetween>
      {range(2).map(i => (
        <Item className={itemVariants()} key={i}>
          <ItemAvatar>
            <SkeletonCircle className="size-10" />
          </ItemAvatar>
          <ItemContent className="gap-1">
            <Skeleton className="w-30" />
            <Skeleton className="w-20" />
          </ItemContent>
          <ItemSide>
            <SkeletonCircle className="size-4" />
          </ItemSide>
        </Item>
      ))}
    </FlexColumn>
  )
}
